import { formatName, formatEmail, formatPhone } from '../../lib/format';
import { Children, Fragment, cloneElement, createContext, forwardRef, isValidElement, useContext } from 'react';
import StatusBadge, { tableText } from './StatusBadge';
import ActionMenu, { collectRowActions } from './ActionMenu';
import './tables.css';
const Columns = createContext([])
const Row = createContext(false)
const classes = (...values) => values.filter(Boolean).join(' ')
function flatten(children) {
    return Children.toArray(children).flatMap(child => isValidElement(child) && child.type === Fragment ? flatten(child.props.children) : [child])
}
function headers(children, inHead = false) {
    return flatten(children).flatMap(child => {
        if (!isValidElement(child)) return []
        if (child.type === TableHeaderCell) return inHead ? [{ label: tableText(child.props.children), kind: child.props.kind }] : []
        if (child.type === Table) return []
        return headers(child.props.children, inHead || child.type === TableHead)
    })
}
function columnKind(label = '') {
    if (/^(actions?|open)$/i.test(label)) return 'actions'
    if (/^(time slot|scheduled hours|rate period)$/i.test(label)) return 'text'
    if (/^(worker|team member|first name|last name|person|sent by|recorded by|by)$/i.test(label)) return 'person'
    if (/email/i.test(label)) return 'email'
    if (/^(phone|mobile|contact number|number)$/i.test(label)) return 'phone'
    if (/^description$/i.test(label)) return 'description'
    if (/^(invoice number|allocation id|number|phone|mobile)$/i.test(label)) return 'number'
    if (/^status$/i.test(label)) return 'status'
    if (/date|time|expiry|^due$|^issued$|^paid$|follow-up|^start$|^finish$|shift start|shift end|^month$|^period$|^when$/i.test(label)) return 'date'
    if (/total|amount|price|rate|hours|mins|quantity|kilomet|odometer|balance|cost|workers required|required workers|service kms|logged$|^connected$|sent$|scheduled$|revenue|profit|count|^gst$/i.test(label)) return 'numeric'
    if (/asset id/i.test(label)) return 'identifier'
    return ''
}
export const Table = forwardRef(function Table({ children, compact = false, emptyItems, className = '', ...props }, ref) {
    const body = Children.map(children, child => {
        if (!emptyItems || !isValidElement(child) || child.type !== 'tbody') return child
        const rows = flatten(child.props.children).filter(item => typeof item !== 'string' || item.trim())
        return rows.length ? child : cloneElement(child, {}, <TableEmpty items={emptyItems} />)
    })
    return <Columns.Provider value={headers(children)}><div className="uiTableViewport"><table {...props} ref={ref} className={classes('uiTable', compact && 'uiTable--compact', className)}>{body}</table></div></Columns.Provider>
})
export const TableHead = forwardRef(function TableHead({ className = '', ...props }, ref) {
    return <thead {...props} ref={ref} className={classes('uiTableHead', className)} />
})
export const TableHeaderCell = forwardRef(function TableHeaderCell({ kind, className = '', children, ...props }, ref) {
    return <th scope="col" {...props} ref={ref} className={classes('uiTableHeaderCell', 'uiTableCell--' + (kind || columnKind(tableText(children))), className)}>{children}</th>
})
export const TableRow = forwardRef(function TableRow({ flagged = false, children, className = '', ...props }, ref) {
    const columns = useContext(Columns)
    const attention = flagged || /recordRowOverdue|workersDirectoryPendingRow/.test(className)
    let index = 0
    const cells = flatten(children).map(child => {
        if (!isValidElement(child)) return child
        const column = columns[index]
        index += Number(child.props.colSpan) || 1
        return child.type === TableCell ? cloneElement(child, { column: column?.label, kind: child.props.kind || column?.kind }) : child
    })
    return <Row.Provider value={attention}><tr {...props} ref={ref} className={classes('uiTableRow', attention && 'uiTableRow--flagged', className)}>{cells}</tr></Row.Provider>
})
export const TableCell = forwardRef(function TableCell({ column, kind, overdue = false, secondary = false, className = '', children, ...props }, ref) {
    const flagged = useContext(Row)
    const type = kind || columnKind(column || props['data-label']) || (typeof children === 'number' ? 'numeric' : '')
    const empty = children == null || children === '' || children === '-' || children === '—'
    let content = empty ? <span className="uiTableEmptyValue">—</span> : children
    if (!empty && typeof children === 'string') {
        if (type === 'person') content = formatName(children)
        if (type === 'phone') content = formatPhone(children)
        if (type === 'email') content = formatEmail(children)
    }
    if (type === 'status' && !empty) {
        const parts = Children.toArray(children)
        content = parts.map((part, index) => index === 0 || (isValidElement(part) && /status/i.test(part.props.className || '')) ? <StatusBadge key={index}>{part}</StatusBadge> : part)
    }
    if (type === 'description' && !empty) content = <span className="uiTableDescription" title={tableText(children)}>{content}</span>
    if (type === 'actions' && !empty) {
        const actions = collectRowActions(children)
        if (actions?.length > 1) content = <ActionMenu>{actions}</ActionMenu>
    }
    return <td {...props} ref={ref} className={classes('uiTableCell', type && 'uiTableCell--' + type, secondary && 'uiTableSecondary', (overdue || (flagged && /due/i.test(column || className))) && 'uiTableCell--overdue', className)}>{type === 'actions' ? <div className="uiTableActions">{content}</div> : content}</td>
})
export const TableEmpty = forwardRef(function TableEmpty({ colSpan, items = 'records', children, className = '', ...props }, ref) {
    const columns = useContext(Columns)
    return <tr {...props} ref={ref} className={classes('uiTableRow', className)}><td colSpan={colSpan || columns.length || 1} className="uiTableEmpty">{children || `No ${items} yet.`}</td></tr>
})
export default Table
